import React, { useMemo } from "react";

/**
 * Operation selector — UI v2 (chips).
 *
 * Same contract as before: receives the full MAFIA table rows (`data`),
 * the current selection (`selectedOpNum`: number | "All" | "Uncategorized"
 * | null) and calls `onSelect` with exactly those value shapes — the
 * parent's filtering logic is untouched.
 *
 * Legacy reference: Inspect.vb listBoxOp (left pane, single click to
 * filter the DNA list). Chips give the same one-tap behavior with the
 * active op always visible — no dropdown to open.
 *
 * Batch 2 / Round 9 rule preserved: op 0 and NULL both belong to
 * "Uncategorized" (legacy never listed 0 as a numeric operation).
 */
const OpNumSelector = ({ data = [], selectedOpNum, onSelect }) => {
  const { opNumbers, hasUncategorized } = useMemo(() => {
    const opNumberSet = new Set();
    let uncategorized = false;

    (Array.isArray(data) ? data : []).forEach((row) => {
      const op = row?.MOMOperationNum;
      if (
        op === null ||
        op === undefined ||
        String(op).trim() === "" ||
        Number(op) === 0
      ) {
        uncategorized = true;
      } else {
        opNumberSet.add(Number(op));
      }
    });

    return {
      opNumbers: [...opNumberSet].sort((a, b) => a - b),
      hasUncategorized: uncategorized,
    };
  }, [data]);

  const isActive = (val) =>
    selectedOpNum === val ||
    (typeof val === "number" && Number(selectedOpNum) === val);

  const chip = (val, label) => (
    <button
      key={String(val)}
      type="button"
      className={[
        "di-op-chip",
        isActive(val) ? "di-op-chip--active" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => onSelect?.(val)}
    >
      {label}
    </button>
  );

  return (
    <div className="di-panel">
      <div className="di-panel__head">
        <span>Operation</span>
        <span>{opNumbers.length + (hasUncategorized ? 1 : 0)}</span>
      </div>
      <div className="di-panel__body">
        {opNumbers.length === 0 && !hasUncategorized ? (
          <div className="text-muted small">No operations found.</div>
        ) : (
          <div className="di-op-chips">
            {chip("All", "All")}
            {opNumbers.map((op) => chip(op, `Op ${op}`))}
            {hasUncategorized && chip("Uncategorized", "Uncategorized")}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(OpNumSelector);
